import { useState, useEffect } from "react";
import {
  _get_restaurant_bikers_list,
  _get_restaurant_deliveries,
  _add_restaurant_biker,
  _remove_restaurant_biker,
} from "../../../common/axios_services";

const useRestaurantDelivery = (restaurantUserId, openNotificationWithIcon) => {
  const [bikerList, setBikerList] = useState([]);
  const [todayDeliveries, setTodayDeliveries] = useState([]);
  const [previousDeliveries, setPreviousDeliveries] = useState([]);
  const [totalBikers, setTotalBikers] = useState(0);
  const [totalTodayDeliveries, setTotalTodayDeliveries] = useState(0);
  const [totalPreviousDeliveries, setTotalPreviousDeliveries] = useState(0);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  const fetchBikerListData = async (page = 1, pageSize = 10) => {
    setSpinnerLoading(true);
    try {
      const response = await _get_restaurant_bikers_list(restaurantUserId, {
        page,
        pageSize,
      });

      setBikerList(response.data?.data?.data || []);
      setTotalBikers(response?.data?.data?.total);
    } catch (err) {
      console.error("Error fetching biker list:", err);
      openNotificationWithIcon(
        "error",
        "Error",
        err.response?.data?.message || "Failed to load biker list."
      );
    } finally {
      setSpinnerLoading(false);
    }
  };

  const fetchTodayDeliveriesData = async (page = 1, pageSize = 10) => {
    setSpinnerLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0]

      const response = await _get_restaurant_deliveries({
        page,
        pageSize,
        date: today,
        restaurantUserId,
      });

      const deliveries = response.data?.data?.orders?.map(order => ({
        id: order.delivery.id,
        sn: order.id,
        pickup_time: order.delivery.createdAt,
        biker_name: `${order.biker?.user?.firstName || ''} ${order.biker?.user?.lastName || ''}`.trim(),
        order_code: order.orderCode,
        delivery_fee: parseFloat(order.delivery?.deliveryFee || 0),
        status: order.delivery?.deliveryStatus?.toLowerCase() || 'pending',
      })) || [];

      setTodayDeliveries(deliveries);
      setTotalTodayDeliveries(response.data?.pagination?.total || 0);
    } catch (err) {
      console.error("Error fetching today's deliveries:", err);
      openNotificationWithIcon(
        "error",
        "Error",
        err.response?.data?.message || "Failed to load today's deliveries."
      );
    } finally {
      setSpinnerLoading(false);
    }
  };

  const fetchPreviousDeliveriesData = async (page = 1, pageSize = 10) => {
    setSpinnerLoading(true);
    try {
      const yesterdayString = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString().split('T')[0];

      const response = await _get_restaurant_deliveries({
        page,
        pageSize,
        dateFrom:"2020-01-01",
        dateTo:yesterdayString,
        restaurantUserId,
      });

      setPreviousDeliveries(response.data?.data?.orders || []);
      setTotalPreviousDeliveries(response.data?.pagination.total);
    } catch (err) {
      console.error("Error fetching previous deliveries:", err);
      openNotificationWithIcon(
        "error",
        "Error",
        err.response?.data?.message || "Failed to load previous deliveries."
      );
    } finally {
      setSpinnerLoading(false);
    }
  };

  const addBiker = async (restaurantId, bikerData) => {
    setSpinnerLoading(true);
    try {
      const response = await _add_restaurant_biker(restaurantId, bikerData);
      openNotificationWithIcon(
        "success",
        "Success",
        "Biker added successfully"
      );
      fetchBikerListData();
      return { success: true, data: response };
    } catch (err) {
      openNotificationWithIcon(
        "error",
        "Error",
        err.response?.data?.message || "Failed to add biker"
      );
      return { success: false, error: err };
    } finally {
      setSpinnerLoading(false);
    }
  };

  const removeBiker = async (restaurantId, bikerId) => {
    setSpinnerLoading(true);
    try {
      const response = await _remove_restaurant_biker(restaurantId, bikerId);
      openNotificationWithIcon(
        "success",
        "Success",
        "Biker removed successfully"
      );
      fetchBikerListData();
      return { success: true, data: response };
    } catch (err) {
      openNotificationWithIcon(
        "error",
        "Error",
        err.response?.data?.message || "Failed to remove biker"
      );
      return { success: false, error: err };
    } finally {
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    fetchBikerListData();
    fetchTodayDeliveriesData();
    fetchPreviousDeliveriesData();
  }, []);

  return {
    bikerList,
    todayDeliveries,
    previousDeliveries,
    totalBikers,
    totalTodayDeliveries,
    totalPreviousDeliveries,
    spinnerLoading,
    fetchBikerListData,
    fetchTodayDeliveriesData,
    fetchPreviousDeliveriesData,
    addBiker,
    removeBiker,
  };
};

export default useRestaurantDelivery;
